import type { MouseEvent } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Dialog, DialogClose, DialogDescription, DialogOverlay, DialogPortal, DialogTitle } from './ui/dialog';
import { supportMailto } from '../constants/appAdoption';
import { formatDate } from '../utils/formatDate';

export interface AppDownloadModalProps {
  cutoff: Date;
  storeUrl: string;
  onOpenStore: (event: MouseEvent<HTMLAnchorElement>) => void;
  onContact: (event: MouseEvent<HTMLAnchorElement>) => void;
  onDismiss: () => void;
}

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background';

// Composed by hand: DialogContent's built-in close button has an untranslated "Close" label.
const AppDownloadModal = ({ cutoff, storeUrl, onOpenStore, onContact, onDismiss }: AppDownloadModalProps) => {
  const { t } = useTranslation();
  const date = formatDate(cutoff, { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <Dialog open onOpenChange={(open) => !open && onDismiss()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-lg border bg-background p-6 text-foreground shadow-lg">
          <DialogTitle className="pr-8 text-lg leading-6">{t('Switch to the GPS Merch app')}</DialogTitle>
          <DialogDescription className="text-sm leading-5">
            {t('From {{date}}, the GPS Merch web version will no longer be accessible. Download the GPS Merch mobile app to keep handling your requests.', { date })}
          </DialogDescription>
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onOpenStore}
            className={`flex h-11 items-center justify-center rounded-full bg-foreground px-4 text-sm font-semibold text-background ${FOCUS_RING}`}
          >
            {t('Download')}
          </a>
          <p className="text-center text-sm text-muted-foreground">
            {t("Can't install the app?")}{' '}
            <a
              href={supportMailto(t('Access to the GPS Merch web version'))}
              onClick={onContact}
              className={`inline-flex min-h-11 items-center rounded font-semibold text-foreground underline underline-offset-2 ${FOCUS_RING}`}
            >
              {t('Contact us')}
            </a>
          </p>
          <DialogClose
            aria-label={t('Close')}
            className={`absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full hover:bg-foreground/5 ${FOCUS_RING}`}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </DialogClose>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default AppDownloadModal;
